import { useSyncExternalStore, useEffect, useCallback } from 'react';

type CacheEntry<T> = {
  data: T | null;
  promise: Promise<T> | null;
  error: Error | null;
  lastUpdated: number;
};

const CACHE_KEY = 'cab_query_cache';
let cache: Record<string, CacheEntry<any>> = {};

if (typeof window !== 'undefined') {
  try {
    const saved = localStorage.getItem(CACHE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      Object.keys(parsed).forEach((key) => {
        cache[key] = {
          data: parsed[key].data,
          promise: null,
          error: null,
          lastUpdated: parsed[key].lastUpdated,
        };
      });
    }
  } catch (e) {
    console.error('[useQuery] Failed to parse query cache', e);
  }
}

function saveCache() {
  if (typeof window !== 'undefined') {
    try {
      const toSave: Record<string, any> = {};
      Object.keys(cache).forEach((key) => {
        if (cache[key].data) {
          toSave[key] = {
            data: cache[key].data,
            lastUpdated: cache[key].lastUpdated,
          };
        }
      });
      localStorage.setItem(CACHE_KEY, JSON.stringify(toSave));
    } catch (e) {
      console.error('[useQuery] Failed to save query cache', e);
    }
  }
}

export function clearQueryCache() {
  cache = {};
  if (typeof window !== 'undefined') {
    localStorage.removeItem(CACHE_KEY);
  }
}
const listeners: Record<string, Set<() => void>> = {};

function notify(key: string) {
  if (listeners[key]) {
    listeners[key].forEach((listener) => listener());
  }
}

function subscribe(key: string, listener: () => void) {
  if (!listeners[key]) {
    listeners[key] = new Set();
  }
  listeners[key].add(listener);
  return () => {
    listeners[key].delete(listener);
  };
}

/**
 * Запускає запит у фоні з кешуванням.
 * Якщо запит вже активний (promise не null), він просто повертає його і не робить дублікат.
 */
export function prefetchQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  force = false
): Promise<T> {
  if (!cache[key]) {
    cache[key] = { data: null, promise: null, error: null, lastUpdated: 0 };
  }

  const entry = cache[key];

  // Якщо запит уже в процесі - просто чекаємо його
  if (entry.promise) return entry.promise;

  // Stale time: 10 секунд (щоб при швидкому перемиканні вкладок не спамити бекенд)
  const STALE_TIME = 10000;
  if (!force && entry.data && Date.now() - entry.lastUpdated < STALE_TIME) {
    return Promise.resolve(entry.data);
  }

  const promise = fetcher()
    .then((data) => {
      cache[key] = {
        ...cache[key],
        data,
        error: null,
        promise: null,
        lastUpdated: Date.now(),
      };
      saveCache();
      notify(key);
      return data;
    })
    .catch((error) => {
      cache[key] = { ...cache[key], error, promise: null };
      notify(key);
      throw error;
    });

  cache[key] = { ...cache[key], promise };
  notify(key);

  return promise;
}

/**
 * Дозволяє мутувати локальний кеш (наприклад, для оптимістичного видалення)
 */
export function mutateQuery<T>(key: string, newData: T | ((prev: T | null) => T)) {
  const current = cache[key]?.data || null;
  const data = typeof newData === 'function' ? (newData as Function)(current) : newData;
  cache[key] = { ...cache[key], data, lastUpdated: Date.now() };
  saveCache();
  notify(key);
}

const emptySnapshot: CacheEntry<any> = {
  data: null,
  promise: null,
  error: null,
  lastUpdated: 0,
};

/**
 * Хук для отримання даних з кешу (Stale-While-Revalidate)
 */
export function useQuery<T>(key: string, fetcher: () => Promise<T>, enabled: boolean = true) {
  const subscribeToKey = useCallback((listener: () => void) => subscribe(key, listener), [key]);

  const getSnapshotForKey = useCallback(() => {
    return cache[key] || emptySnapshot;
  }, [key]);

  const state = useSyncExternalStore(subscribeToKey, getSnapshotForKey);

  useEffect(() => {
    if (enabled) {
      prefetchQuery(key, fetcher).catch(() => {});
    }
  }, [key, enabled]);

  return {
    data: state.data as T | null,
    // Показуємо isLoading лише при першому завантаженні (коли ще немає даних у кеші)
    isLoading: !state.data && !!state.promise,
    // isRefetching показує, що дані в кеші є, але у фоні йде оновлення
    isRefetching: !!state.data && !!state.promise,
    error: state.error,
    mutate: (newData: T | ((prev: T | null) => T)) => mutateQuery(key, newData),
    refetch: () => prefetchQuery(key, fetcher, true),
  };
}
